import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Upload, RefreshCw, Trash2, Building2, Download } from "lucide-react";
import { toast } from "sonner";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { TransactionModals } from "@/components/TransactionModals";
import { TransactionFilters } from "@/components/transactions/TransactionFilters";
import { TransactionStatsCards } from "@/components/transactions/TransactionStatsCards";
import { BulkActionBar } from "@/components/transactions/BulkActionBar";
import { TransactionTable } from "@/components/transactions/TransactionTable";
import { ClearTransactionsModal } from "@/components/transactions/ClearTransactionsModal";
import { exportTransactionsToCsv } from "@/components/transactions/exportTransactions";
import { devWarn } from "../utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TDSTransactions = () => {
  const { selectedClient, clientId } = useClient();
  const { canEdit, canViewOnly: _canViewOnly } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sections, setSections] = useState([]);
  const [periods, setPeriods] = useState({ financial_years: [], quarters: [], months: [] });
  const [selectedFY, setSelectedFY] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [periodType, setPeriodType] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showExcluded, setShowExcluded] = useState(false);
  const [showMismatchOnly, setShowMismatchOnly] = useState(false);
  const [showNoPanOnly, setShowNoPanOnly] = useState(false);
  const [showNoPartyNameOnly, setShowNoPartyNameOnly] = useState(false);
  const [selectedSections, setSelectedSections] = useState([]);
  const fileInputRef = useRef(null);

  // Selection state for bulk actions
  const [selectedTxns, setSelectedTxns] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Modal states
  const [exclusionModal, setExclusionModal] = useState({ open: false, txnId: null, txnName: "", isBulk: false });
  const [exclusionReason, setExclusionReason] = useState("");
  const [sectionModal, setSectionModal] = useState({ open: false, txn: null, isBulk: false });
  const [newSection, setNewSection] = useState("");
  const [rateChoice, setRateChoice] = useState("");
  const [panModal, setPanModal] = useState({ open: false, partyName: "", currentPan: "", txnId: null });
  const [newPan, setNewPan] = useState("");
  const [partyNameModal, setPartyNameModal] = useState({ open: false, oldPartyName: "", txnId: null });
  const [newPartyName, setNewPartyName] = useState("");
  const [newPartyPan, setNewPartyPan] = useState("");
  const [partySearchQuery, setPartySearchQuery] = useState("");
  const [existingParties, setExistingParties] = useState([]);
  const [showPartySearch, setShowPartySearch] = useState(false);
  const [bulkPartyModal, setBulkPartyModal] = useState({ open: false });
  const [bulkPartyName, setBulkPartyName] = useState("");
  const [bulkPan, setBulkPan] = useState("");
  const [tdsModal, setTdsModal] = useState({ open: false, txn: null, applyTo: "single" });
  const [newAmount, setNewAmount] = useState("");
  const [newTdsAmount, setNewTdsAmount] = useState("");
  const [showSectionDropdown, setShowSectionDropdown] = useState(false);
  const [clearModalOpen, setClearModalOpen] = useState(false);

  const fetchPeriods = useCallback(async () => {
    try {
      const params = clientId ? { client_id: clientId } : {};
      const response = await axios.get(`${API}/periods`, { params });
      setPeriods(response.data);
    } catch (err) { devWarn("fetchPeriods (non-critical):", err); }
  }, [clientId]);

  const fetchTransactions = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const params = { client_id: clientId };
      if (search) params.party = search;
      if (sectionFilter && sectionFilter !== "all") params.section = sectionFilter;
      if (selectedFY && selectedFY !== "all") params.financial_year = selectedFY;
      if (periodType === "quarter" && selectedQuarter && selectedQuarter !== "all") params.quarter = selectedQuarter;
      if (periodType === "month" && selectedMonth && selectedMonth !== "all") params.month = selectedMonth;
      if (periodType === "custom" && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      const response = await axios.get(`${API}/transactions`, { params });
      setTransactions(response.data);
      setSelectedTxns(new Set());
      setSelectAll(false);
    } catch (err) {
      toast.error(`Failed to load transactions: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [clientId, search, sectionFilter, selectedFY, periodType, selectedQuarter, selectedMonth, customStartDate, customEndDate]);

  const fetchSections = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reference/tds-sections`);
      setSections(response.data);
    } catch (err) { devWarn("fetchSections (non-critical):", err); }
  }, []);

  const fetchExistingParties = useCallback(async () => {
    if (!clientId) return;
    try {
      const response = await axios.get(`${API}/ledgers?client_id=${clientId}`);
      setExistingParties(response.data || []);
    } catch (err) { devWarn("fetchExistingParties (non-critical):", err); }
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    queueMicrotask(() => {
      fetchPeriods();
      fetchSections();
      fetchExistingParties();
    });
  }, [clientId, fetchPeriods, fetchSections, fetchExistingParties]);

  useEffect(() => {
    if (clientId) {
      queueMicrotask(() => fetchTransactions());
    } else {
      queueMicrotask(() => setTransactions([]));
    }
  }, [clientId, fetchTransactions]);

  const handleSearch = (e) => { e.preventDefault(); fetchTransactions(); };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !clientId) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        setUploading(true);
        const text = event.target.result;
        const lines = text.split("\n").filter((line) => line.trim());
        const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
        const data = lines.slice(1).map((line) => {
          const values = line.split(",");
          const obj = {};
          headers.forEach((header, idx) => {
            const val = values[idx]?.trim();
            if (header.includes("date")) obj.date = val;
            else if (header.includes("party") || header.includes("name")) obj.party_name = val;
            else if (header.includes("pan")) obj.pan = val;
            else if (header.includes("tds") && header.includes("ledger")) obj.tds_ledger = val;
            else if (header.includes("amount") && !header.includes("tds")) obj.amount = parseFloat(val) || 0;
            else if (header.includes("tds") && (header.includes("amount") || header.includes("deducted"))) obj.tds_amount = parseFloat(val) || 0;
            else if (header.includes("voucher")) obj.voucher_no = val;
          });
          return obj;
        }).filter((item) => item.party_name && item.amount);
        const response = await axios.post(`${API}/transactions/bulk?client_id=${clientId}`, data);
        toast.success(`Uploaded ${response.data.inserted} transactions`);
        fetchTransactions();
        fetchPeriods();
      } catch (err) { toast.error("Failed to upload"); }
      finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsText(file);
  };

  const handleClearAll = () => setClearModalOpen(true);
  const handleClearConfirmed = () => {
    setTransactions([]);
    fetchPeriods();
    fetchTransactions();
  };

  // Memoized filtered transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter((txn) => {
      if (!showExcluded && txn.is_excluded) return false;
      if (showMismatchOnly && !txn.rate_mismatch) return false;
      if (showNoPanOnly && txn.pan && txn.pan.trim() !== "") return false;
      if (showNoPartyNameOnly && txn.party_name && txn.party_name.trim() !== "") return false;
      if (selectedSections.length > 0) {
        const txnSection = txn.user_section || txn.section || "";
        if (!selectedSections.includes(txnSection)) return false;
      }
      return true;
    });
  }, [transactions, showExcluded, showMismatchOnly, showNoPanOnly, showNoPartyNameOnly, selectedSections]);

  // Split memos to reduce dep count per original code quality concerns
  const activeTransactions = useMemo(
    () => filteredTransactions.filter((t) => !t.is_excluded),
    [filteredTransactions]
  );
  const totals = useMemo(
    () => activeTransactions.reduce(
      (acc, txn) => ({ amount: acc.amount + (txn.amount || 0), tds: acc.tds + (txn.tds_amount || 0) }),
      { amount: 0, tds: 0 }
    ),
    [activeTransactions]
  );
  const counts = useMemo(() => ({
    mismatch: transactions.filter((t) => t.rate_mismatch && !t.is_excluded).length,
    excluded: transactions.filter((t) => t.is_excluded).length,
    noPan: transactions.filter((t) => !t.pan || t.pan.trim() === "").length,
    noPartyName: transactions.filter((t) => !t.party_name || t.party_name.trim() === "").length,
  }), [transactions]);

  const availableSections = useMemo(
    () => [...new Set(transactions.map((t) => t.user_section || t.section).filter(Boolean))].sort(),
    [transactions]
  );

  // Selection handlers
  const handleSelectAll = useCallback((checked) => {
    setSelectAll(checked);
    if (checked) {
      const allIds = filteredTransactions.filter((t) => !t.is_excluded).map((t) => t.id);
      setSelectedTxns(new Set(allIds));
    } else {
      setSelectedTxns(new Set());
    }
  }, [filteredTransactions]);

  const handleSelectTxn = useCallback((txnId, checked) => {
    setSelectedTxns((prev) => {
      const newSet = new Set(prev);
      if (checked) newSet.add(txnId);
      else newSet.delete(txnId);
      const selectableCount = filteredTransactions.filter((t) => !t.is_excluded).length;
      setSelectAll(newSet.size === selectableCount && selectableCount > 0);
      return newSet;
    });
  }, [filteredTransactions]);

  // Exclusion handlers
  const openExclusionModal = useCallback((txn, isBulk = false) => {
    if (isBulk) {
      setExclusionModal({ open: true, txnId: null, txnName: `${selectedTxns.size} transactions`, isBulk: true });
    } else {
      setExclusionModal({ open: true, txnId: txn.id, txnName: txn.party_name, isBulk: false });
    }
    setExclusionReason("");
  }, [selectedTxns.size]);

  const handleExclude = async () => {
    if (!exclusionReason.trim() || exclusionReason.trim().length < 3) {
      toast.error("Please provide a reason (min 3 characters)");
      return;
    }
    try {
      if (exclusionModal.isBulk) {
        await axios.put(`${API}/transactions/bulk/exclude`, {
          transaction_ids: Array.from(selectedTxns),
          reason: exclusionReason,
        });
        toast.success(`${selectedTxns.size} transactions excluded`);
        setSelectedTxns(new Set());
        setSelectAll(false);
      } else {
        await axios.put(`${API}/transactions/${exclusionModal.txnId}/exclude`, { reason: exclusionReason });
        toast.success("Transaction excluded");
      }
      setExclusionModal({ open: false, txnId: null, txnName: "", isBulk: false });
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to exclude");
    }
  };

  const handleRestore = useCallback(async (txnId) => {
    try {
      await axios.put(`${API}/transactions/${txnId}/restore`);
      toast.success("Transaction restored");
      fetchTransactions();
    } catch (err) { toast.error("Failed to restore"); }
  }, [fetchTransactions]);

  const handleBulkRestore = async () => {
    const excludedIds = filteredTransactions.filter((t) => t.is_excluded).map((t) => t.id);
    if (excludedIds.length === 0) {
      toast.info("No excluded transactions to restore");
      return;
    }
    try {
      const response = await axios.put(`${API}/transactions/bulk/restore`, { transaction_ids: excludedIds });
      toast.success(`${response.data.restored} transactions restored`);
      fetchTransactions();
    } catch (err) { toast.error("Failed to bulk restore"); }
  };

  const openBulkPartyModal = () => {
    setBulkPartyModal({ open: true });
    setBulkPartyName("");
    setBulkPan("");
  };

  const handleBulkUpdateParty = async () => {
    if (!bulkPartyName && !bulkPan) {
      toast.error("Please enter party name or PAN");
      return;
    }
    try {
      const response = await axios.put(`${API}/transactions/bulk/update-party`, {
        transaction_ids: Array.from(selectedTxns),
        party_name: bulkPartyName.trim() || null,
        pan: bulkPan.trim() || null,
      });
      let message = `Updated ${response.data.updated} transactions`;
      if (response.data.party_name) message += ` - Name: ${response.data.party_name}`;
      if (response.data.pan) message += ` - PAN: ${response.data.pan}`;
      toast.success(message);
      setBulkPartyModal({ open: false });
      setSelectedTxns(new Set());
      setSelectAll(false);
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  // Section edit
  const openSectionModal = useCallback((txn, isBulk = false) => {
    if (isBulk) {
      setSectionModal({ open: true, txn: null, isBulk: true });
    } else {
      setSectionModal({ open: true, txn, isBulk: false });
    }
    setNewSection(isBulk ? "" : (txn?.user_section || txn?.section || ""));
    setRateChoice("");
  }, []);

  const handleUpdateSection = async () => {
    if (!newSection) { toast.error("Please select a section"); return; }
    try {
      if (sectionModal.isBulk) {
        const response = await axios.put(`${API}/transactions/bulk/section`, {
          transaction_ids: Array.from(selectedTxns),
          section: newSection,
          rate_choice: rateChoice || null,
        });
        const mismatchMsg = response.data.mismatches > 0 ? ` (${response.data.mismatches} mismatches)` : "";
        toast.success(`Updated ${response.data.updated} transactions to ${newSection}${mismatchMsg}`);
        setSelectedTxns(new Set());
        setSelectAll(false);
      } else {
        const response = await axios.put(`${API}/transactions/${sectionModal.txn.id}/section`, {
          section: newSection,
          rate_choice: rateChoice || null,
        });
        toast.success(`Section updated. Expected rate: ${response.data.expected_rate}%`);
      }
      setSectionModal({ open: false, txn: null, isBulk: false });
      fetchTransactions();
    } catch (err) { toast.error("Failed to update section"); }
  };

  // PAN update
  const openPanModal = useCallback((partyName, currentPan) => {
    setPanModal({ open: true, partyName, currentPan: currentPan || "" });
    setNewPan("");
  }, []);

  const handleUpdatePan = async () => {
    if (!newPan.trim() || newPan.trim().length !== 10) {
      toast.error("Please enter a valid 10-character PAN");
      return;
    }
    try {
      const response = await axios.put(`${API}/party/update-pan`, {
        party_name: panModal.partyName,
        pan: newPan.trim().toUpperCase(),
      }, { params: { client_id: clientId } });
      toast.success(`PAN updated for ${panModal.partyName}. ${response.data.transactions_updated} transactions updated.`);
      setPanModal({ open: false, partyName: "", currentPan: "", txnId: null });
      setNewPan("");
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update PAN");
    }
  };

  // Party name update
  const openPartyNameModal = useCallback((txn) => {
    setPartyNameModal({ open: true, oldPartyName: txn.party_name || "", txnId: txn.id });
    setNewPartyName("");
    setNewPartyPan("");
  }, []);

  const handleUpdatePartyName = async () => {
    if (!newPartyName.trim()) { toast.error("Please enter a party name"); return; }
    try {
      const isBlankPartyName = !partyNameModal.oldPartyName || partyNameModal.oldPartyName.trim() === "";
      const response = await axios.put(`${API}/party/update-name`, {
        old_party_name: partyNameModal.oldPartyName,
        new_party_name: newPartyName.trim(),
        pan: newPartyPan.trim() || null,
        transaction_id: isBlankPartyName ? partyNameModal.txnId : null,
      }, { params: { client_id: clientId } });
      const panMsg = response.data.pan_updated ? " (PAN also updated)" : "";
      toast.success(`Party name updated. ${response.data.transactions_updated} transaction(s) updated${panMsg}.`);
      setPartyNameModal({ open: false, oldPartyName: "", txnId: null });
      setNewPartyName("");
      setNewPartyPan("");
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update party name");
    }
  };

  // TDS Amount edit
  const openTdsModal = useCallback((txn) => {
    setTdsModal({ open: true, txn, applyTo: "single" });
    setNewAmount(txn.amount?.toString() || "");
    setNewTdsAmount(txn.tds_amount?.toString() || "");
  }, []);

  const handleUpdateTdsAmount = async () => {
    const amount = parseFloat(newAmount) || 0;
    const tdsAmount = parseFloat(newTdsAmount) || 0;
    if (tdsAmount < 0) { toast.error("TDS amount cannot be negative"); return; }
    try {
      const response = await axios.put(`${API}/transaction/update-tds`, {
        transaction_id: tdsModal.applyTo === "single" ? tdsModal.txn.id : null,
        voucher_no: tdsModal.applyTo === "voucher" ? tdsModal.txn.voucher_no : null,
        party_name: tdsModal.applyTo === "party" ? tdsModal.txn.party_name : null,
        new_amount: amount,
        new_tds_amount: tdsAmount,
        apply_to: tdsModal.applyTo,
      }, { params: { client_id: clientId } });
      let applyMsg = "";
      if (tdsModal.applyTo === "voucher") {
        applyMsg = ` for voucher ${tdsModal.txn.voucher_no}`;
      } else if (tdsModal.applyTo === "party") {
        applyMsg = ` for party ${tdsModal.txn.party_name}`;
      }
      toast.success(`TDS updated! ${response.data.transactions_updated} transaction(s) modified${applyMsg}`);
      setTdsModal({ open: false, txn: null, applyTo: "single" });
      setNewAmount("");
      setNewTdsAmount("");
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update TDS amount");
    }
  };

  const getAffectedCount = useCallback(() => {
    if (!tdsModal.txn) return 0;
    if (tdsModal.applyTo === "single") return 1;
    if (tdsModal.applyTo === "voucher") {
      return transactions.filter((t) => t.voucher_no === tdsModal.txn.voucher_no).length;
    }
    if (tdsModal.applyTo === "party") {
      return transactions.filter((t) => t.party_name === tdsModal.txn.party_name).length;
    }
    return 0;
  }, [tdsModal, transactions]);

  const toggleSectionFilter = useCallback((section) => {
    setSelectedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  }, []);

  const resetFilters = () => {
    setSearch(""); setSectionFilter(""); setSelectedFY(""); setSelectedQuarter(""); setSelectedMonth("");
    setPeriodType("all"); setCustomStartDate(""); setCustomEndDate(""); setSelectedSections([]);
    setShowSectionDropdown(false);
    fetchTransactions();
  };

  const handleExportExcel = () => exportTransactionsToCsv({
    activeTransactions, filteredTransactions, totals, selectedClient, selectedFY, selectedQuarter,
  });

  // Row handlers bundled for table
  const rowHandlers = useMemo(() => ({
    openPartyNameModal, openPanModal, openTdsModal, openSectionModal,
    openExclusionModal, handleRestore,
  }), [openPartyNameModal, openPanModal, openTdsModal, openSectionModal, openExclusionModal, handleRestore]);

  if (!selectedClient) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500">
        <Building2 className="w-16 h-16 mb-4 text-slate-300" />
        <p className="text-lg font-medium">No Client Selected</p>
      </div>
    );
  }

  const canEditNow = canEdit();

  return (
    <div data-testid="tds-transactions-page">
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title">TDS Transactions</h1>
          <p className="page-subtitle">{selectedClient.name} - Monthly TDS data</p>
        </div>
        <div className="flex gap-2">
          {canEditNow && <input type="file" accept=".csv,.txt" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />}
          <Button onClick={handleExportExcel} variant="outline" disabled={activeTransactions.length === 0} data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" />Export to Excel
          </Button>
          {canEditNow && (
            <>
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-csv-btn">
                {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload CSV
              </Button>
              <Button variant="outline" onClick={handleClearAll} data-testid="clear-all-btn">
                <Trash2 className="w-4 h-4 mr-2" />Clear
              </Button>
            </>
          )}
        </div>
      </div>

      <TransactionFilters
        search={search} setSearch={setSearch}
        periods={periods}
        selectedFY={selectedFY} setSelectedFY={setSelectedFY}
        periodType={periodType} setPeriodType={setPeriodType}
        selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth}
        selectedQuarter={selectedQuarter} setSelectedQuarter={setSelectedQuarter}
        customStartDate={customStartDate} setCustomStartDate={setCustomStartDate}
        customEndDate={customEndDate} setCustomEndDate={setCustomEndDate}
        sectionFilter={sectionFilter} setSectionFilter={setSectionFilter}
        showSectionDropdown={showSectionDropdown} setShowSectionDropdown={setShowSectionDropdown}
        showExcluded={showExcluded} setShowExcluded={setShowExcluded} excludedCount={counts.excluded}
        showMismatchOnly={showMismatchOnly} setShowMismatchOnly={setShowMismatchOnly} mismatchCount={counts.mismatch}
        showNoPanOnly={showNoPanOnly} setShowNoPanOnly={setShowNoPanOnly} noPanCount={counts.noPan}
        showNoPartyNameOnly={showNoPartyNameOnly} setShowNoPartyNameOnly={setShowNoPartyNameOnly} noPartyNameCount={counts.noPartyName}
        availableSections={availableSections} selectedSections={selectedSections}
        toggleSectionFilter={toggleSectionFilter} setSelectedSections={setSelectedSections}
        filteredTransactions={filteredTransactions}
        handleSearch={handleSearch} onReset={resetFilters}
        onBulkRestore={handleBulkRestore} canEdit={canEditNow}
      />

      {canEditNow && (
        <BulkActionBar
          selectedTxns={selectedTxns}
          setSelectedTxns={setSelectedTxns}
          setSelectAll={setSelectAll}
          onBulkUpdateParty={openBulkPartyModal}
          onBulkAssignSection={() => openSectionModal(null, true)}
          onBulkExclude={() => openExclusionModal(null, true)}
        />
      )}

      {transactions.length > 0 && (
        <TransactionStatsCards
          activeCount={activeTransactions.length}
          totals={totals}
          mismatchCount={counts.mismatch}
        />
      )}

      <TransactionTable
        filteredTransactions={filteredTransactions}
        loading={loading}
        canEdit={canEditNow}
        selectedTxns={selectedTxns}
        selectAll={selectAll}
        handleSelectAll={handleSelectAll}
        handleSelectTxn={handleSelectTxn}
        handlers={rowHandlers}
      />

      <ClearTransactionsModal
        open={clearModalOpen}
        onClose={() => setClearModalOpen(false)}
        onConfirmed={handleClearConfirmed}
        clientId={clientId}
        financialYear={selectedFY}
        quarter={periodType === "quarter" ? selectedQuarter : null}
        month={periodType === "month" ? selectedMonth : null}
      />

      <TransactionModals
        exclusionModal={exclusionModal} setExclusionModal={setExclusionModal}
        exclusionReason={exclusionReason} setExclusionReason={setExclusionReason}
        handleExclude={handleExclude}
        sectionModal={sectionModal} setSectionModal={setSectionModal}
        newSection={newSection} setNewSection={setNewSection}
        rateChoice={rateChoice} setRateChoice={setRateChoice}
        sections={sections}
        handleSectionUpdate={handleUpdateSection}
        panModal={panModal} setPanModal={setPanModal}
        newPan={newPan} setNewPan={setNewPan}
        handlePanUpdate={handleUpdatePan}
        partyNameModal={partyNameModal} setPartyNameModal={setPartyNameModal}
        newPartyName={newPartyName} setNewPartyName={setNewPartyName}
        newPartyPan={newPartyPan} setNewPartyPan={setNewPartyPan}
        showPartySearch={showPartySearch} setShowPartySearch={setShowPartySearch}
        partySearchQuery={partySearchQuery} setPartySearchQuery={setPartySearchQuery}
        handlePartyNameUpdate={handleUpdatePartyName}
        tdsModal={tdsModal} setTdsModal={setTdsModal}
        newAmount={newAmount} setNewAmount={setNewAmount}
        newTdsAmount={newTdsAmount} setNewTdsAmount={setNewTdsAmount}
        handleTdsAmountUpdate={handleUpdateTdsAmount}
        getAffectedCount={getAffectedCount}
        bulkPartyModal={bulkPartyModal} setBulkPartyModal={setBulkPartyModal}
        bulkPartyName={bulkPartyName} setBulkPartyName={setBulkPartyName}
        bulkPan={bulkPan} setBulkPan={setBulkPan}
        handleBulkPartyUpdate={handleBulkUpdateParty}
        selectedTxns={selectedTxns} existingParties={existingParties}
        transactions={transactions}
      />
    </div>
  );
};

export default TDSTransactions;
